import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      // Don't redirect here, let components handle it
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  processSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
};

// Products
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getBySlug: (slug) => api.get(`/products/${slug}`),
  getFeatured: () => api.get('/products', { params: { featured: true } }),
};

// Quotes
export const quotesAPI = {
  create: (data) => api.post('/quotes', data),
  getAll: () => api.get('/quotes'),
  getById: (id) => api.get(`/quotes/${id}`),
  requestSample: (data) => api.post('/quotes/sample', data),
};

// Upload
export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getFile: (fileId) => `${API_BASE}/upload/${fileId}`,
};

// Blog
export const blogAPI = {
  getAll: (params) => api.get('/blog', { params }),
  getBySlug: (slug) => api.get(`/blog/${slug}`),
};

// Public
export const publicAPI = {
  getUseCases: () => api.get('/public/usecases'),
  getUseCase: (slug) => api.get(`/public/usecases/${slug}`),
  getFAQ: () => api.get('/public/faq'),
};

// Admin
export const adminAPI = {
  // Products
  getProducts: (params) => api.get('/admin/products', { params }),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  
  // Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  
  // Quotes
  getQuotes: (params) => api.get('/admin/quotes', { params }),
  updateQuote: (id, data) => api.put(`/admin/quotes/${id}`, data),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  
  // Blog
  getPosts: (params) => api.get('/admin/blog', { params }),
  createPost: (data) => api.post('/admin/blog', data),
  updatePost: (id, data) => api.put(`/admin/blog/${id}`, data),
  
  // Samples
  getSamples: (params) => api.get('/admin/samples', { params }),
  updateSample: (id, data) => api.put(`/admin/samples/${id}`, data),
  
  // Use Cases
  getUseCases: () => api.get('/admin/usecases'),
  createUseCase: (data) => api.post('/admin/usecases', data),
  updateUseCase: (id, data) => api.put(`/admin/usecases/${id}`, data),
  
  // FAQ
  createFAQ: (data) => api.post('/admin/faq', data),
  updateFAQ: (id, data) => api.put(`/admin/faq/${id}`, data),
};

// Seed data
export const seedData = () => api.post('/seed');

export default api;
